import React, { useRef, useState, useEffect } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import useTranslateLpc from '@components/common/hooks/useTranslateLpc';
import useWindowSize from '@components/common/hooks/useWindowSize';
import { M_DEVICE } from '@components/lpc/Constants';
import AmountModule from '@components/lpc/Pot/AmountModule/AmountModule';
import ThankParticipants from '@components/lpc/Pot/AmountModule/ThankParticipants/ThankParticipants';
import Participants from '@components/lpc/Pot/MainModule/Activity/Participants/Participants';
import MainModule from '@components/lpc/Pot/MainModule/MainModule';
import ReportPot from '@components/lpc/Pot/MainModule/ReportPot/ReportPot';
import PotHeaderBanner from '@components/lpc/Pot/PotHeaderBanner/PotHeaderBanner';
import { personRoles } from '@models/PotModel/personRoles';
import { partnerType } from '@propTypes/partnerTypes';
import { potObjectiveType, potType } from '@propTypes/potTypes';
import { scrollToRef } from '@services/utils/DomService';
import ObjectService from '@services/utils/ObjectService';

import css from './ModuleContainer.module.scss';

type TabContainerProps = {
    pot: potType;
    mobileView?: boolean;
    onToggleOpenSettings: (boolean) => void;
    setOpenParticipate: (boolean) => void;
    favoritePartners?: partnerType[];
    newPoolObjective: (potObjectiveType) => void;
    objective?: potObjectiveType;
};

const TourProvider: any = dynamic(() => import('reactour'), {
    ssr: false,
});

const ModuleContainer = ({
    pot,
    mobileView,
    onToggleOpenSettings,
    newPoolObjective,
    setOpenParticipate,
}: TabContainerProps) => {
    const [goToParticipantsList, setGoToParticipantsList] = useState<boolean>(false);
    const [isTourOpen, setIsTourOpen] = useState<boolean>(false);
    const [openSetting, setOpenSetting] = useState<boolean>(false);
    const containerRef = useRef(null);
    const { t } = useTranslateLpc(['lpc-pot']);
    const { width } = useWindowSize();
    const isMobile = width <= M_DEVICE;
    const router = useRouter();

    useEffect(() => {
        goToParticipantsList && scrollToRef(containerRef);
    }, [goToParticipantsList]);

    useEffect(() => {
        if (pot.personRole === personRoles.ORGANISER && !router?.query?.participate) {
            setIsTourOpen(pot.firstTimeOnPotBoard);
        }
    }, []);

    const closeTour = () => {
        document.body.style.overflow = 'scroll';
        setIsTourOpen(false);
    };

    const openTour = () => {
        document.body.style.overflow = 'hidden';
        setIsTourOpen(true);
    };

    const tourConfig = [
        {
            selector: isMobile ? '.DescriptionMobile' : '.Description',
            content: ({ goTo }) => (
                <>
                    <div className={css.dot_container__dot_1}>
                        <div className={css.dot_container__dot}>
                            <div className={css.dot_container__pink_dot} />
                        </div>
                    </div>
                    <div className={css.helper__title}>{t('tutorial.step-1.title')}</div>
                    <div className={css.helper__steps}>{t('tutorial.step-1.steps')}</div>
                    <div className={css.helper__container}>
                        <div className={css.helper__description}>{t('tutorial.step-1.description')}</div>
                    </div>
                    <div className={css.buttons_container}>
                        <button
                            className={`${css.tutorial__btn_help} ${css.buttons_container__skip}`}
                            onClick={() => closeTour()}
                        >
                            {t('tutorial.skip')}
                        </button>
                        <button
                            className={isMobile ? css.buttons_container__next_only : css.buttons_container__next}
                            onClick={() => {
                                goTo(1);
                            }}
                        >
                            {t('tutorial.next')}
                        </button>
                    </div>
                </>
            ),
            position: isMobile ? 'bottom' : 'right',
            style: {
                height: isMobile ? '248px' : '205px',
                width: isMobile ? '95%' : '482px',
            },
        },
        {
            selector: '.Settings',
            content: ({ goTo }) => (
                <div>
                    <div className={css.dot_container__dot_2}>
                        <div className={css.dot_container__dot}>
                            <div className={css.dot_container__pink_dot} />
                        </div>
                    </div>
                    <div className={css.helper__title}>{t('tutorial.step-2.title')}</div>
                    <div className={css.helper__steps}>{t('tutorial.step-2.steps')}</div>
                    <div className={css.helper__container}>
                        <div className={css.helper__description}>{t('tutorial.step-2.description-1')}</div>
                        <div className={css.helper__description}>{t('tutorial.step-2.description-2')}</div>
                    </div>
                    <button
                        id="box"
                        className={`${css.tutorial__btn_help} ${css.buttons_container__skip}`}
                        onClick={() => closeTour()}
                    >
                        {t('tutorial.skip')}
                    </button>
                    <div className={css.buttons_container}>
                        <button className={css.buttons_container__previous} onClick={() => goTo(0)}>
                            {t('tutorial.previous')}
                        </button>
                        <button
                            className={css.buttons_container__next}
                            onClick={() => {
                                isMobile ? onToggleOpenSettings(true) : setOpenSetting(true);
                                setTimeout(() => {
                                    openTour();
                                    goTo(2);
                                }, 200);
                            }}
                        >
                            {t('tutorial.next')}
                        </button>
                    </div>
                </div>
            ),
            position: 'bottom',
            style: {
                height: isMobile ? '285px' : '280px',
                width: isMobile ? '95%' : '642px',
            },
        },
        {
            selector: '.Objective',
            content: ({ goTo }) => (
                <div>
                    <div className={css.dot_container__dot_3}>
                        <div className={css.dot_container__dot}>
                            <div className={css.dot_container__pink_dot} />
                        </div>
                    </div>
                    <div className={css.helper__title}>{t('tutorial.step-3.title')}</div>
                    <div className={css.helper__steps}>{t('tutorial.step-3.steps')}</div>
                    <div className={css.helper__container}>
                        <div className={css.helper__description}>{t('tutorial.step-3.description-1')}</div>
                        <div className={css.helper__description}>{t('tutorial.step-3.description-2')}</div>
                        <div className={css.helper__description}>{t('tutorial.step-3.description-3')}</div>
                    </div>
                    <div className={css.buttons_container}>
                        <button
                            className={`${css.tutorial__btn_help} ${css.buttons_container__skip}`}
                            onClick={() => {
                                isMobile ? onToggleOpenSettings(false) : setOpenSetting(false);
                                closeTour();
                            }}
                        >
                            {t('tutorial.skip')}
                        </button>
                        <button
                            className={css.buttons_container__previous}
                            onClick={() => {
                                isMobile ? onToggleOpenSettings(false) : setOpenSetting(false);
                                goTo(1);
                            }}
                        >
                            {t('tutorial.previous')}
                        </button>
                        <button
                            className={css.buttons_container__next}
                            onClick={() => {
                                isMobile ? onToggleOpenSettings(false) : setOpenSetting(false);
                                closeTour();
                            }}
                        >
                            {t('tutorial.finish')}
                        </button>
                    </div>
                </div>
            ),
            position: isMobile ? 'top' : 'bottom',
            style: {
                height: isMobile ? '280px' : '380px',
                width: isMobile ? '85%' : '622px',
            },
        },
    ];

    return (
        <div className={css.page}>
            {pot.personRole === personRoles.ORGANISER && width >= M_DEVICE && (
                <TourProvider
                    onRequestClose={closeTour}
                    steps={tourConfig}
                    startAt={0}
                    showNavigation={false}
                    showButtons={false}
                    isOpen={isTourOpen}
                    showNumber={false}
                    closeWithMask={false}
                    rounded={10}
                    maskSpace={10}
                    disableInteraction={true}
                    className={css.helper}
                    disableKeyboardNavigation={true}
                    showCloseButton={false}
                />
            )}
            <div className={css.wrapper}>
                {pot.personRole === personRoles.ORGANISER && !isMobile && (
                    <PotHeaderBanner
                        desc={t('activity.banner.desc')}
                        pot={pot}
                        newPoolObjective={(objective) => newPoolObjective(objective)}
                        openSetting={openSetting}
                    />
                )}
                <div className={`${css.container} ${css.container__first}`}>
                    <div className={css.tab_container}>
                        <section ref={containerRef} className={`${css.tab} ${css.tab__main}`}>
                            <MainModule
                                personRole={pot.personRole}
                                pot={pot}
                                mobileView={mobileView}
                                goToParticipant={goToParticipantsList}
                                onGoToParticipantsList={() => setGoToParticipantsList(false)}
                                newPoolObjective={(objective) => newPoolObjective(objective)}
                                openSetting={openSetting}
                                setOpenParticipate={(value) => setOpenParticipate(value)}
                            />
                        </section>
                        <ReportPot personRole={pot.personRole} potRef={pot.uniq} />
                    </div>
                    <div className={css.tab_container}>
                        <section className={`${css.tab} ${css.tab__small}`}>
                            <AmountModule
                                onGoToParticipantsList={() => setGoToParticipantsList(true)}
                                personRole={pot.personRole}
                                pot={pot}
                                potStatus={pot.status}
                                setOpenParticipate={(value) => setOpenParticipate(value)}
                            />
                            {pot.personRole === personRoles.ORGANISER && width >= M_DEVICE && (
                                <div
                                    className={
                                        ObjectService.hasContent(pot.eventDate)
                                            ? `${css.tab__very_small}`
                                            : `${css.tab__very_small}  ${css.tab__very_small__custom}`
                                    }
                                >
                                    <div className={css.tutorial}>
                                        <button className={css.tutorial__btn_help} onClick={() => openTour()}>
                                            <img
                                                className={css.tutorial__action_help}
                                                src={`/front-static/icons/pot/icons-help.png`}
                                                alt="help"
                                            />
                                        </button>
                                    </div>
                                </div>
                            )}
                        </section>
                        {(pot.privacy.showParticipantsList || pot.personRole === personRoles.ORGANISER) &&
                            !isMobile && (
                                <section className={`${css.tab} ${css.tab__small}`}>
                                    <Participants
                                        pot={pot}
                                        participationNumber={pot.participationNumber}
                                        personRole={pot.personRole}
                                        potRef={pot.uniq}
                                        privacy={pot.privacy}
                                        poolContribution={pot.poolContribution}
                                        setOpenParticipate={(value) => setOpenParticipate(value)}
                                    />
                                    <ThankParticipants
                                        buttonVariant={'secondary'}
                                        isVisible={pot.personRole === personRoles.BENEFICIARY && !isMobile}
                                        potRef={pot.uniq}
                                        wordingCta={t('amount.cta.thank-participants-explicit')}
                                        potTitle={pot.title}
                                        customClass={css.tab__thank}
                                    />
                                </section>
                            )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModuleContainer;
